// Designated Safeguarding Lead (DSL) designation.
// A nursery names one DSL and, optionally, one deputy. Holding the designation
// opens the safeguarding-specific records (accident and incident log, complaints
// log, LADO referrals, social care case flags) whatever the person's base role,
// and grants nothing else at all.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNurseryId } from "@/hooks/useNurseryId";
import { useAuth } from "@/contexts/AuthContext";
import { friendlyError } from "@/lib/friendlyError";
import type { SuccessorNomination } from "@/hooks/useSuccession";

export type DslSlot = "primary" | "deputy";

/** Is the signed-in person the DSL or deputy DSL? */
export function useAmIDsl() {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["am-i-dsl", user?.id],
    enabled: !!user?.id,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("am_i_dsl");
      if (error) throw error;
      return !!data;
    },
  });
  return { isDsl: !!data, isLoading };
}

/** Confirmed safeguarding leads recorded on the nursery (set only after acceptance). */
export function useDesignatedDsls() {
  const nurseryId = useNurseryId();
  return useQuery({
    queryKey: ["designated-dsls", nurseryId],
    enabled: !!nurseryId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("nurseries")
        .select("dsl_staff_id, dsl_deputy_staff_id, dsl_set_at")
        .eq("id", nurseryId!)
        .maybeSingle();
      if (error) throw error;
      return data as {
        dsl_staff_id: string | null;
        dsl_deputy_staff_id: string | null;
        dsl_set_at: string | null;
      } | null;
    },
  });
}

export function useDslNominations() {
  const nurseryId = useNurseryId();
  return useQuery({
    queryKey: ["dsl-nominations", nurseryId],
    enabled: !!nurseryId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("successor_nominations")
        .select("*")
        .eq("kind", "dsl")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as SuccessorNomination[];
    },
  });
}

/** The safeguarding lead request waiting on the signed-in person, if any. */
export function useMyDslNomination() {
  const { user } = useAuth();
  const { data, isLoading } = useDslNominations();
  const mine = (data ?? []).find(
    (n) =>
      n.nominee_user_id === user?.id &&
      n.status === "pending" &&
      new Date(n.expires_at) > new Date(),
  );
  return { nomination: mine ?? null, isLoading };
}

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["dsl-nominations"] });
  qc.invalidateQueries({ queryKey: ["designated-dsls"] });
  qc.invalidateQueries({ queryKey: ["am-i-dsl"] });
  qc.invalidateQueries({ queryKey: ["notifications"] });
}

export function useNominateDsl() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async ({ staffId, slot }: { staffId: string; slot: DslSlot }) => {
      const { data, error } = await supabase.rpc("dsl_nominate", {
        p_staff_id: staffId,
        p_slot: slot,
      });
      if (error) throw error;
      const nominationId = data as unknown as string;
      // Best effort email, the in-platform notification is already saved.
      await supabase.functions
        .invoke("notify-successor-nomination", { body: { nominationId } })
        .catch(() => undefined);
      return nominationId;
    },
    onSuccess: () => {
      invalidate(qc);
      toast({
        title: "Request sent ✅",
        description:
          "They have 14 days to accept or decline. Nothing changes until they accept.",
      });
    },
    onError: (error: Error) =>
      toast({
        title: "Could not send the request",
        description: friendlyError(error),
        variant: "destructive",
      }),
  });
}

export function useWithdrawDsl() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (slot: DslSlot) => {
      const { error } = await supabase.rpc("dsl_withdraw", { p_slot: slot });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate(qc);
      toast({
        title: "Safeguarding lead removed",
        description: "Their access to the safeguarding records goes back to their usual level.",
      });
    },
    onError: (error: Error) =>
      toast({
        title: "Could not remove the safeguarding lead",
        description: friendlyError(error),
        variant: "destructive",
      }),
  });
}
